/**
 * isInternalPath — the one same-origin rule for any path that reaches
 * navigate(). Moved out of useSmartBack.js (#4556) so React-free modules such
 * as resolvePostLoginRedirect.js can use it without pulling in react-router.
 * useSmartBack.js re-exports it for existing importers.
 */

/**
 * A path is "internal" (safe to navigate to in-app) only if it is a single-'/'-
 * prefixed string with no authority-forming bypasses. A browser / WHATWG URL
 * parser turns several "/-prefixed" strings into an EXTERNAL origin, so each is
 * rejected:
 *   '//evil.com'     protocol-relative                         → external
 *   '/\evil.com'     backslash normalized to '/' → '//evil.com' → external
 *   '/\n/evil.com'   embedded tab/LF/CR stripped → '//evil.com' → external
 *
 * Implemented WITHOUT `new URL(p, window.location.origin)` deliberately: this
 * hook is unit-tested via renderToStaticMarkup (no jsdom), where `window` is
 * undefined. The character checks below are env-agnostic and cover the same
 * bypasses the URL parser would otherwise let through.
 */
export function isInternalPath(p) {
  if (typeof p !== 'string' || p.length === 0) return false;
  // Must start with exactly one '/'. Reject '//' (protocol-relative) and '/\'
  // (backslash the browser normalizes to '/', i.e. '/\evil' → '//evil').
  if (p[0] !== '/' || p[1] === '/' || p[1] === '\\') return false;
  // Reject ASCII control chars (incl. tab \x09 / LF \x0A / CR \x0D), DEL, and any
  // backslash: the URL parser strips control chars mid-string and can re-form an
  // external authority (e.g. '/\n/evil.com' → '//evil.com').
  // eslint-disable-next-line no-control-regex -- intentional: these are exactly the chars URL parsing strips
  if (/[\x00-\x1F\x7F\\]/.test(p)) return false;
  return true;
}
